import   React              ,
       { useMemo             }  from 'react';

import   classNames            from 'classnames';
import   'flag-icons/css/flag-icons.min.css';

import   styles                from './AdminRow.module.scss';

import   Icon                  from '../../widget/images/Icon';

import { Admin               }  from '../../../dataTypes/adminManagment/types';
import { getAdminPhotoUrl    }  from '../../../backendServices/adminManagment/api';
import { getInitials         ,
         generateInitialsAvatarDataUrl ,
         formatSubscriberCount         ,
         formatDateOrDash              }  from '../../../dataTypes/adminManagment/utils';
import { COUNTRY_LIST         }  from '../../../dataTypes/adminManagment/countries';
import { formatStorageSize    }  from '../../../utils/utils_storage';
import { estSansLimite        }  from '../../../dataTypes/adminManagment/quota';

// Statut du compte : rond coloré + libellé de la même couleur, indépendant de "verified"
const ACCOUNT_STATUS_INFO: Record<string, { label: string; className: string }> = {
  active    : { label: 'Actif'      , className: styles.status_active    },
  pending   : { label: 'En attente' , className: styles.status_pending   },
  suspended : { label: 'Suspendu'   , className: styles.status_suspended },
  disabled  : { label: 'Désactivé'  , className: styles.status_disabled  },
  deleted   : { label: 'Supprimé'    , className: styles.status_disabled  },
};

interface AdminRowProps {
  admin    : Admin;
  index    : number;
  onEdit   : (index: number) => void;
  onDelete : (admin: Admin) => void;
}

export const AdminRow = ({ admin    ,
                           index    ,
                           onEdit   ,
                           onDelete }: AdminRowProps) => {

  const photoUrl = getAdminPhotoUrl(admin);

  // Pas de photo ? Même avatar généré (rond bleu + initiales) que dans AdminSheet.
  const defaultPhotoImage = useMemo(
    () => generateInitialsAvatarDataUrl(getInitials(admin.firstname, admin.lastname, admin.pseudonym)),
    [admin.firstname, admin.lastname, admin.pseudonym]
  );

  const avatarSrc = photoUrl !== '' ? photoUrl : defaultPhotoImage;

  const countryCode = COUNTRY_LIST.find((country) => country.name === admin.country)?.code;

  const statusInfo = ACCOUNT_STATUS_INFO[admin.accountStatus] ?? ACCOUNT_STATUS_INFO.active;

  return (

    <div className={classNames(styles.row)}>

      <div className={classNames(styles.column_creator, styles.standard)}>
        {
         avatarSrc !== ''
         ? <img src       = {avatarSrc}
                alt       = {`${admin.firstname} ${admin.lastname}`}
                className = {styles.thumbnail}
                onClick   = {() => onEdit(index)} />
         : <div className={styles.thumbnailPlaceholder} onClick={() => onEdit(index)}>
             <Icon name       = "user"
                   color      = "var(--color-Icon-FG)"
                   hoverColor = "var(--color-Icon-hover-FG)"
                   width      = {28}
                   height     = {28}
                   hoverable  = {false}
                   clickable  = {false} />
           </div>
        }
        <div className={classNames(styles.creatorInfo)}>
          <span className={classNames(styles.creatorName, styles.creatorNameLink)}
                onClick={() => onEdit(index)}>
            {`${admin.firstname} ${admin.lastname}`.trim() || '—'}
          </span>
          {
           admin.email &&
           <span className={classNames(styles.creatorSlug)}>
             {admin.email}
           </span>
          }
        </div>
      </div>

      <div className={classNames(styles.column_3, styles.standard)}>
        <div className={classNames(styles.creatorInfo)}>
          <span className={classNames(styles.creatorName)}>
            {admin.pseudonym || '—'}
          </span>
          {
           admin.slug !== '' &&
           <span className={classNames(styles.creatorSlug)}>@{admin.slug}</span>
          }
        </div>
      </div>

      <div className={classNames(styles.column_4, styles.standard)}>
        {
         admin.verified
         ? (
          <div className={classNames(styles.statusBadge, styles.statusBadge_verified)}>
            <Icon name="Check" color="#FFFFFF" hoverable={false} clickable={false} width={16} height={16} />
          </div>
         )
         : (
          <div className={classNames(styles.statusBadge, styles.statusBadge_unverified)}>
            <Icon name="Minus" color="#FFFFFF" hoverable={false} clickable={false} width={16} height={16} />
          </div>
         )
        }
      </div>

      <div className={classNames(styles.column_5, styles.standard)}>
        {formatSubscriberCount(admin.nbSubscribers)}
      </div>

      {/* Heures de diffusion (chaîne allumée) et nombre de diffusions, cumulés depuis le 11/10/2026. */}
      <div className={classNames(styles.column_hours, styles.standard)}
           title={`${admin.broadcastSeconds} s de chaîne allumée depuis le 11/10/2026`}>
        {admin.broadcastSeconds > 0 ? `${(admin.broadcastSeconds / 3600).toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} h` : '—'}
      </div>

      <div className={classNames(styles.column_sessions, styles.standard)}>
        {admin.broadcastSessions > 0 ? admin.broadcastSessions : '—'}
      </div>

      {/* Stockage utilisé (octets, tel que le serveur le tient) sur le quota du créateur. */}
      <div className={classNames(styles.column_storage, styles.standard)}
           title={`${admin.quotaUsed} octets utilisés`}>
        <span>{formatStorageSize(admin.quotaUsed, new Date())}</span>
        <span className={styles.storageQuota}>/ {estSansLimite(admin.quotaDisk) ? '∞' : formatStorageSize(admin.quotaDisk, new Date())}</span>
      </div>

      <div className={classNames(styles.column_6, styles.standard)}>
        {
         countryCode || admin.city
         ? (
          <span className={classNames(styles.locationCell)}>
            {countryCode && <span className={classNames('fi', `fi-${countryCode}`, styles.flag)} />}
            <span>{admin.city || '—'}</span>
          </span>
         )
         : '—'
        }
      </div>

      <div className={classNames(styles.column_status, styles.standard, statusInfo.className)}>
        <span className={classNames(styles.statusDot)} />
        <span className={classNames(styles.statusLabel)}>{statusInfo.label}</span>
      </div>

      <div className={classNames(styles.column_creationDate, styles.standard)}>
        {formatDateOrDash(admin.creationDate)}
      </div>

      <div className={classNames(styles.column_lastLogin, styles.standard)}>
        {formatDateOrDash(admin.lastLoginDate)}
      </div>

      <div className={classNames(styles.column_7, styles.standard)}>
        <div className={styles.iconButton} onClick={() => onEdit(index)}>
          <Icon name       = "pen"
                color      = "var(--color-Icon-FG)"
                hoverColor = "var(--color-Icon-hover-FG)"
                width      = {20}
                height     = {20} />
        </div>
        {
         admin.accountStatus !== 'deleted' &&
         <div className={styles.iconButton} onClick={() => onDelete(admin)}>
           <Icon name       = "trashCan"
                 color      = "var(--color-Icon-FG)"
                 hoverColor = "var(--color-Icon-hover-FG)"
                 width      = {20}
                 height     = {20} />
         </div>
        }
      </div>

    </div>
  );
};

export default AdminRow;
